import { Component, State, h, Prop, Watch } from '@stencil/core';
import state from '@ugc-store';

import { getProductReviewSummarySentence, getTagsV2 } from '@ugc-api/review';

import { LinedThumbsDown, LinedThumbsUp, MinusCircle, Ai } from '../../icons';

const enum TagClassification {
  Positive,
  Neutral,
  Negative,
}

function mapTags(tags = []) {
  return tags
    .map(({ name, classification }) => ({ name, classification }))
    .sort((a, b) => a.classification - b.classification);
}

@Component({
  tag: 'ugc-review-tagsv2',
  styleUrl: 'ugc-review-tagsv2.scss',
  shadow: false,
})
export class UgcReviewTags {
  @Prop() productId: number;
  @State() items: { name: string; classification: TagClassification }[] = [];
  @State() summarySentence: string = '';

  async getAiTags(productId, params) {
    try {
      const { tags = [] } = await getTagsV2(`${productId}`, params);
      return tags;
    } catch (e) {}
  }

  async getTagsData() {
    const {
      preference: { language },
      isEnableTagsV2,
    } = state;

    if (!isEnableTagsV2) return;

    const getAiTagsPromise = () => this.getAiTags(this.productId, { lc: language, count: 10 });

    await getAiTagsPromise().then(aiTags => {
      this.items = mapTags(aiTags);
    });
  }

  async getSummarySentence() {
    const {
      preference: { language },
      isEnableSummary,
    } = state;

    if (!isEnableSummary) return;

    await getProductReviewSummarySentence({
      productId: this.productId,
      languageCode: language,
    }).then(({ content = '' }) => {
      this.summarySentence = content;
    });
  }

  async componentDidLoad() {
    await Promise.all([this.getTagsData(), this.getSummarySentence()]);
  }

  @Watch('productId')
  async watchProductIdHandler() {
    this.getTagsData();
    this.getSummarySentence();
  }

  handleClickTag = name => {
    const {
      productInfo: { productId, urlName = '' },
    } = state;
    window.location.href = `${state.domains.catalog || ''}/r/${urlName}/${productId}?search=${name}`;
  };

  render() {
    const {
      reviewStaticLanguage: {
        IDS_REVIEW_SUMMARY_CUSTOMERS_SAY = '',
        IDS_UGC_SUMMARIZATION_TOOLTIP_CONTENT = '',
        IDS_LNK_CLOSE = '',
        IDS_UGC_REVIEW_HIGHLIGHTS = '',
        IDS_MSG_SHOW_MORE = '',
        IDS_MSG_SHOW_LESS = '',
      } = {},
      isDesktop,
    } = state;
    const showSummary = !!this.summarySentence;
    const showTags = !!this.items.length;
    const showTagsTitle = showSummary && showTags;

    if (!showTags && !showSummary) return null;

    return (
      <div
        class={`${isDesktop ? 'p-16 mb-24 rounded-16' : 'rounded-8 mt-16 p-12'}`}
        style={{ background: 'linear-gradient(180deg, #E2F2FF 0%, #FFFFFF 70%)' }}
      >
        {/* summary part */}
        <div class="flex-row-start-center gap-8 flex-wrap">
          <Ai />
          <h4 class={`${isDesktop ? 'fs-20 lh-30' : 'fs-16 lh-24'} m-0`}>{IDS_REVIEW_SUMMARY_CUSTOMERS_SAY}</h4>
          <div class="flex-row-start-center gap-4 c-666">
            <ugc-info-tooltip
              header={IDS_REVIEW_SUMMARY_CUSTOMERS_SAY}
              content={IDS_UGC_SUMMARIZATION_TOOLTIP_CONTENT}
              closeText={IDS_LNK_CLOSE}
              tooltipName="summary-sentence-tooltip"
              iconSize="16px"
            ></ugc-info-tooltip>
          </div>
        </div>
        {showSummary ? (
          <div class={`mt-8`}>
            <ugc-ellipsis-text
              buttonStyle={{ fontSize: isDesktop ? '16px' : '14px' }}
              contentStyle={{ fontSize: isDesktop ? '16px' : '14px', lineHeight: isDesktop ? '24px' : '20px' }}
              iconSize={isDesktop ? '24' : '20'}
              line={isDesktop ? 3 : 6}
              text={this.summarySentence}
              readMore={IDS_MSG_SHOW_MORE}
              readLess={IDS_MSG_SHOW_LESS}
            />
          </div>
        ) : null}

        {/* tags part */}
        {showTags ? (
          <div>
            {showTagsTitle ? (
              <div class={`${isDesktop ? 'fs-16 lh-24' : 'fs-14 lh-20'} color-gray-700 bold mt-16`}>
                {IDS_UGC_REVIEW_HIGHLIGHTS}
              </div>
            ) : null}
            <div class="tags">
              {this.items.map(({ name, classification }) => (
                <a
                  data-ga-event-category="ugc"
                  data-ga-event-action="review filter tag"
                  data-ga-event-label={name}
                  data-ga-event-name="ugc_reviews"
                  class={`tag-item ${
                    classification === TagClassification.Positive
                      ? 'tag-positive'
                      : classification === TagClassification.Negative
                      ? 'tag-negative'
                      : ''
                  }`}
                  onClick={() => {
                    this.handleClickTag(name);
                  }}
                >
                  <div class="flex shrink-0">
                    {classification === TagClassification.Positive ? (
                      <LinedThumbsUp width="20" height="20" />
                    ) : classification === TagClassification.Negative ? (
                      <LinedThumbsDown width="20" height="20" />
                    ) : (
                      <MinusCircle />
                    )}
                  </div>

                  <span class="truncate">{name}</span>
                </a>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}
